/**
 * In-memory cache for project item and action item list responses.
 * Entries expire after five minutes. Every write to a project's items or action items drops that
 * project's entries (see `authenticatedFetch` in config/api.ts), so a saved change is never hidden by the cache.
 */

const TTL_MS = 5 * 60 * 1000;

export type ItemListKind = 'items' | 'actionItems';

const entries = new Map<string, { value: unknown; storedAt: number }>();

/** Builds a cache key from the list kind, the project and the full query. */
export const itemListCacheKey = (kind: ItemListKind, projectId: string, query: object): string =>
  `${kind}|${projectId}|${JSON.stringify(query)}`;

/** Returns a cached list response, or undefined when there is none or it has expired. */
export const getCachedItemList = <T,>(key: string): T | undefined => {
  const entry = entries.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.storedAt > TTL_MS) {
    entries.delete(key);
    return undefined;
  }
  return entry.value as T;
};

export const setCachedItemList = (key: string, value: unknown): void => {
  entries.set(key, { value, storedAt: Date.now() });
};

/** Drops every cached list of one project. */
export const invalidateItemListCache = (projectId: string): void => {
  for (const key of entries.keys()) {
    if (key.split('|')[1] === projectId) entries.delete(key);
  }
};
